import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesClientesAPI } from '../../lib/ordenesApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { ColumnSelector } from '../../components/ui/column-selector';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
    Users,
    Plus,
    Search,
    ArrowLeft,
    Eye,
    Edit,
    Trash2,
    Building2,
    Loader2,
} from 'lucide-react';

const CLIENT_COLUMNS = [
    { key: 'rut', label: 'RUT', required: true, defaultVisible: true },
    { key: 'razon_social', label: 'Razón Social', defaultVisible: true },
    { key: 'nombre_fantasia', label: 'Nombre Fantasía', defaultVisible: false },
    { key: 'direccion', label: 'Dirección', defaultVisible: false },
    { key: 'comuna', label: 'Comuna', defaultVisible: false },
    { key: 'ciudad', label: 'Ciudad', defaultVisible: true },
    { key: 'telefono', label: 'Teléfono', defaultVisible: false },
    { key: 'email', label: 'Email', defaultVisible: false },
    { key: 'contacto', label: 'Contacto', defaultVisible: true },
    { key: 'giro', label: 'Giro', defaultVisible: false },
];

const initialFormState = {
    rut: '',
    razon_social: '',
    nombre_fantasia: '',
    direccion: '',
    comuna: '',
    ciudad: '',
    telefono: '',
    email: '',
    contacto_principal: '',
    giro: '',
};

export default function OrdenesClientesPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [clientes, setClientes] = useState([]);
    const [search, setSearch] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [editingCliente, setEditingCliente] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);
    
    // Selección múltiple
    const [selectedIds, setSelectedIds] = useState([]);
    
    // Columnas visibles
    const [visibleColumns, setVisibleColumns] = useState(
        CLIENT_COLUMNS.filter(c => c.defaultVisible !== false).map(c => c.key)
    );

    useEffect(() => {
        loadClientes();
    }, [search]);

    const loadClientes = async () => {
        try {
            setLoading(true);
            const res = await ordenesClientesAPI.getAll(search || undefined);
            setClientes(res.data);
            setSelectedIds([]); // Limpiar selección al recargar
        } catch (err) {
            console.error('Error loading clientes:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingCliente(null);
        setFormData(initialFormState);
        setShowDialog(true);
    };

    const handleOpenEdit = (cliente) => {
        setEditingCliente(cliente);
        setFormData({
            rut: cliente.rut || '',
            razon_social: cliente.razon_social || '',
            nombre_fantasia: cliente.nombre_fantasia || '',
            direccion: cliente.direccion || '',
            comuna: cliente.comuna || '',
            ciudad: cliente.ciudad || '',
            telefono: cliente.telefono || '',
            email: cliente.email || '',
            contacto_principal: cliente.contacto_principal || '',
            giro: cliente.giro || '',
        });
        setShowDialog(true);
    };

    const handleOpenDelete = (cliente) => {
        setEditingCliente(cliente);
        setShowDeleteDialog(true);
    };

    const handleSave = async () => {
        if (!formData.rut || !formData.razon_social) {
            toast.error('RUT y Razón Social son requeridos');
            return;
        }

        setSaving(true);
        try {
            if (editingCliente) {
                await ordenesClientesAPI.update(editingCliente.id, formData);
                toast.success('Cliente actualizado');
            } else {
                await ordenesClientesAPI.create(formData);
                toast.success('Cliente creado');
            }
            setShowDialog(false);
            loadClientes();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await ordenesClientesAPI.delete(editingCliente.id);
            toast.success('Cliente eliminado');
            setShowDeleteDialog(false);
            loadClientes();
        } catch (err) {
            toast.error('Error al eliminar cliente');
        } finally {
            setSaving(false);
        }
    };

    // Selección múltiple
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedIds(clientes.map(c => c.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id, checked) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter(i => i !== id));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        
        setSaving(true);
        try {
            const res = await ordenesClientesAPI.deleteBulk(selectedIds);
            toast.success(res.data.message);
            setShowBulkDeleteDialog(false);
            setSelectedIds([]);
            loadClientes();
        } catch (err) {
            toast.error('Error al eliminar clientes');
        } finally {
            setSaving(false);
        }
    };

    const allSelected = clientes.length > 0 && selectedIds.length === clientes.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < clientes.length;

    if (permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Users className="h-6 w-6" />
                            Clientes
                        </h1>
                        <p className="text-muted-foreground">Gestión de clientes y empresas</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {selectedIds.length > 0 && tienePermiso('eliminar_clientes') && (
                        <Button 
                            variant="destructive" 
                            onClick={() => setShowBulkDeleteDialog(true)}
                            data-testid="bulk-delete-btn"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar ({selectedIds.length})
                        </Button>
                    )}
                    {tienePermiso('crear_clientes') && (
                        <Button onClick={handleOpenCreate} data-testid="new-cliente-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Nuevo Cliente
                        </Button>
                    )}
                </div>
            </div>

            {/* Search */}
            <div className="flex gap-4 items-center">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por RUT, razón social..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-input"
                    />
                </div>
                <ColumnSelector
                    columns={CLIENT_COLUMNS}
                    visibleColumns={visibleColumns}
                    onVisibilityChange={setVisibleColumns}
                    storageKey="clientes-columns"
                />
            </div>

            {/* Table */}
            <Card className="border-2">
                <CardContent className="p-0 overflow-x-auto">
                    {loading ? (
                        <div className="p-8">
                            <Skeleton className="h-64 w-full" />
                        </div>
                    ) : clientes.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>No hay clientes registrados</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">
                                        <Checkbox 
                                            checked={allSelected}
                                            ref={(el) => el && (el.indeterminate = someSelected)}
                                            onCheckedChange={handleSelectAll}
                                            data-testid="select-all-checkbox"
                                        />
                                    </TableHead>
                                    {visibleColumns.includes('rut') && <TableHead>RUT</TableHead>}
                                    {visibleColumns.includes('razon_social') && <TableHead>Razón Social</TableHead>}
                                    {visibleColumns.includes('nombre_fantasia') && <TableHead>Nombre Fantasía</TableHead>}
                                    {visibleColumns.includes('direccion') && <TableHead>Dirección</TableHead>}
                                    {visibleColumns.includes('comuna') && <TableHead>Comuna</TableHead>}
                                    {visibleColumns.includes('ciudad') && <TableHead>Ciudad</TableHead>}
                                    {visibleColumns.includes('telefono') && <TableHead>Teléfono</TableHead>}
                                    {visibleColumns.includes('email') && <TableHead>Email</TableHead>}
                                    {visibleColumns.includes('contacto') && <TableHead>Contacto</TableHead>}
                                    {visibleColumns.includes('giro') && <TableHead>Giro</TableHead>}
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {clientes.map((cliente) => (
                                    <TableRow key={cliente.id} className="cursor-pointer hover:bg-muted/50" data-testid={`cliente-${cliente.id}`}>
                                        <TableCell>
                                            <Checkbox 
                                                checked={selectedIds.includes(cliente.id)}
                                                onCheckedChange={(checked) => handleSelectOne(cliente.id, checked)}
                                            />
                                        </TableCell>
                                        {visibleColumns.includes('rut') && (
                                            <TableCell className="font-mono">{cliente.rut}</TableCell>
                                        )}
                                        {visibleColumns.includes('razon_social') && (
                                            <TableCell className="font-medium">{cliente.razon_social}</TableCell>
                                        )}
                                        {visibleColumns.includes('nombre_fantasia') && (
                                            <TableCell>{cliente.nombre_fantasia || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('direccion') && (
                                            <TableCell>{cliente.direccion || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('comuna') && (
                                            <TableCell>{cliente.comuna || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('ciudad') && (
                                            <TableCell>{cliente.ciudad || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('telefono') && (
                                            <TableCell>{cliente.telefono || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('email') && (
                                            <TableCell>{cliente.email || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('contacto') && (
                                            <TableCell>{cliente.contacto_principal || '-'}</TableCell>
                                        )}
                                        {visibleColumns.includes('giro') && (
                                            <TableCell>{cliente.giro || '-'}</TableCell>
                                        )}
                                        <TableCell>
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => navigate(`/ordenes/clientes/${cliente.id}`)}>
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                {tienePermiso('editar_clientes') && (
                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(cliente)}>
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {tienePermiso('eliminar_clientes') && (
                                                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleOpenDelete(cliente)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
                        <DialogDescription>
                            {editingCliente ? 'Modifica los datos del cliente' : 'Ingresa los datos del nuevo cliente'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                        <div className="space-y-2">
                            <Label>RUT *</Label>
                            <Input
                                value={formData.rut}
                                onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                                placeholder="12.345.678-9"
                                disabled={!!editingCliente}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Razón Social *</Label>
                            <Input
                                value={formData.razon_social}
                                onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                                placeholder="Empresa S.A."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Nombre Fantasía</Label>
                            <Input
                                value={formData.nombre_fantasia}
                                onChange={(e) => setFormData({ ...formData, nombre_fantasia: e.target.value })}
                                placeholder="Mi Empresa"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Giro</Label>
                            <Input
                                value={formData.giro}
                                onChange={(e) => setFormData({ ...formData, giro: e.target.value })}
                                placeholder="Transporte de carga"
                            />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <Label>Dirección</Label>
                            <Input
                                value={formData.direccion}
                                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                                placeholder="Av. Principal 123"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Comuna</Label>
                            <Input
                                value={formData.comuna}
                                onChange={(e) => setFormData({ ...formData, comuna: e.target.value })}
                                placeholder="Santiago"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Ciudad</Label>
                            <Input
                                value={formData.ciudad}
                                onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                                placeholder="Santiago"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Teléfono</Label>
                            <Input
                                value={formData.telefono}
                                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                placeholder="+56 9 1234 5678"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="contacto@empresa.cl"
                            />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <Label>Contacto Principal</Label>
                            <Input
                                value={formData.contacto_principal}
                                onChange={(e) => setFormData({ ...formData, contacto_principal: e.target.value })}
                                placeholder="Juan Pérez"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                'Guardar'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Cliente</DialogTitle>
                        <DialogDescription>
                            ¿Estás seguro de eliminar a <strong>{editingCliente?.razon_social}</strong>? Esta acción no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Eliminando...
                                </>
                            ) : (
                                'Eliminar'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Clientes</DialogTitle>
                        <DialogDescription>
                            ¿Estás seguro de eliminar <strong>{selectedIds.length} clientes</strong>? Esta acción no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button variant="destructive" onClick={handleBulkDelete} disabled={saving}>
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Eliminando...
                                </>
                            ) : (
                                `Eliminar ${selectedIds.length} clientes`
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
